// eslint-disable-next-line spaced-comment
/// <reference types="@core/tellent-mobile-logic" />
import { IdentityHeader } from '@core/http-types';
import {
  AppKind,
  TellentClientAuthErrorCode,
  TellentClientAuthInitData,
  TellentCurrentUser,
  TellentMember,
  TellentUserResponse,
} from '@core/tellent-types';

import {
  authErrorHandlerExhaustiveGuard,
  TellentClientAuthError,
  TellentClientBootstrapError,
  TellentClientMemberNotFoundInOrgError,
  TellentClientMemberSeatNotFoundInOrgError,
  TellentClientOrgGuidMismatchError,
  TellentClientOrgGuidUnavailableError,
  TellentClientTokenInvalidError,
  TellentClientTokenUnavailableError,
  TellentClientUserUnavailableError,
} from './errors';

const headers = [
  ['accept', 'application/json'],
  ['x-json-accent', 'pascal'],
  [IdentityHeader.Client, 'TellentClient'],
];

type TellentClientAuthEndpoints = {
  tellentAuthTokenUrl: string;
  tellentUserUrl: string;
  tellentAuthUrl: string;
};

type AuthorizeOpts = {
  useOrgFallback: boolean;
  throwOnMissingAppGuid: boolean;
};

const defaultOpts: AuthorizeOpts = {
  useOrgFallback: true,
  throwOnMissingAppGuid: true,
};

export async function authorize(
  config: TellentClientAuthEndpoints,
  appKind: `${AppKind}`,
  partialOptions: Partial<AuthorizeOpts> = defaultOpts,
): Promise<TellentClientAuthInitData> {
  const { useOrgFallback, throwOnMissingAppGuid } = {
    ...defaultOpts,
    ...partialOptions,
  };

  const authResJson = await fetchTokenOrThrow(config);
  const userResJson = await fetchUserOrThrow(config, authResJson!.accessToken);

  if (!authResJson || !userResJson) {
    throw new TellentClientBootstrapError();
  }

  const {
    user,
    accessToken: { kind: authKind },
  } = userResJson;
  const { accessToken } = authResJson;

  const orgGuid =
    authResJson.orgGuid ??
    readOrgGuidFromQueryParam() ??
    readOrgGuidFromLocalStorage(user.guid) ??
    (useOrgFallback ? readOrgGuidFallback(user.members) : null);

  if (!orgGuid) {
    throw new TellentClientOrgGuidUnavailableError({
      accessToken,
      user,
      appKind,
    });
  }

  user.currentOrgGuid = orgGuid;
  user.isAuthorizedWith = authKind;
  if (!validateOrgGuid(user, orgGuid)) {
    throw new TellentClientOrgGuidMismatchError({
      accessToken,
      user,
      appKind,
    });
  }

  const member = resolveMember(user, orgGuid);

  if (!member)
    throw new TellentClientMemberNotFoundInOrgError({
      accessToken,
      user,
      orgGuid,
      appKind,
    });

  const appGuid = resolveAppGuid(member, appKind)!;

  // settings (legacy 'admin') app doesn't have appGuid, hence opt out from throw
  if (throwOnMissingAppGuid && !appGuid) {
    throw new TellentClientMemberSeatNotFoundInOrgError({
      accessToken,
      user,
      orgGuid,
      appKind,
    });
  }

  // TODO: What if LS is full? Fallback to other persistance mechanism?
  writeOrgGuidToLocalStorage(user.guid, orgGuid);

  return {
    error: false,
    member,
    user,
    accessToken,
    orgGuid,
    appGuid,
    appKind,
  };
}

type AuthErrorResult = ReturnType<typeof handleAuthError> & { appKind: `${AppKind}` };

export function authorizeErrorHandler(
  error: Error,
  config: TellentClientAuthEndpoints,
  appKind: `${AppKind}`,
): AuthErrorResult | undefined {
  if (error instanceof TellentClientAuthError) {
    const result: ReturnType<typeof handleAuthError> & { appKind: `${AppKind}` } = {
      ...(handleAuthError(error, config) || {}),
      appKind,
    };

    if (result.continueOnError) {
      return result;
    }
  }
}

export function handleAuthError(
  error: TellentClientAuthError,
  config: TellentClientAuthEndpoints,
): Partial<TellentClientAuthInitData> | null | void {
  switch (error.message) {
    case TellentClientAuthErrorCode.BootstrapError:
      throw error;

    case TellentClientAuthErrorCode.TokenUnavailable:
    case TellentClientAuthErrorCode.TokenInvalid:
      redirectToAuth(config);
      return null;

    case TellentClientAuthErrorCode.UserUnavailable:
      /**
       * TODO:
       * redirect to sign out?
       */
      throw error;

    case TellentClientAuthErrorCode.MemberNotFoundInOrg:
    case TellentClientAuthErrorCode.MemberSeatNotFoundInOrg:
    case TellentClientAuthErrorCode.OrgGuidUnavailable:
    case TellentClientAuthErrorCode.OrgGuidMismatch:
      return {
        ...error.payload,
        errorCode: error.message,
      };

    default:
      return authErrorHandlerExhaustiveGuard(error.message);
  }
}

async function fetchTokenOrThrow(
  config: TellentClientAuthEndpoints,
): Promise<{ accessToken: string; orgGuid: string | null } | null> {
  if (window.isNativeMobileHr) {
    const user = await window.nativeMobileHr.fetchTokenFromNative();
    if (!user) {
      throw new TellentClientTokenUnavailableError();
    }
    return user;
  }
  const authHeaders = new Headers();
  headers.forEach(([name, value]) => authHeaders.append(name, value));

  const authRes = await fetch(config.tellentAuthTokenUrl, {
    headers: authHeaders,
    mode: 'cors',
    credentials: 'include',
  });

  if (authRes.status === 401) throw new TellentClientTokenUnavailableError();

  const user = await authRes.json();
  return user;
}

async function fetchUserOrThrow(
  config: TellentClientAuthEndpoints,
  token: string,
): Promise<TellentUserResponse | null> {
  const userHeaders = new Headers();
  headers.forEach(([name, value]) => userHeaders.append(name, value));
  userHeaders.append('Authorization', `Bearer ${token}`);

  const userRes = await fetch(config.tellentUserUrl, {
    headers: userHeaders,
    mode: 'cors',
  });

  if (userRes.status === 401) throw new TellentClientTokenInvalidError();
  if (userRes.status !== 200) throw new TellentClientUserUnavailableError();

  const user = await userRes.json();
  return user;
}

function readOrgGuidFromLocalStorage(userGuid: string) {
  return localStorage.getItem(`preferred_org_guid_${userGuid}`);
}

function readOrgGuidFallback(members: TellentMember[]) {
  return members[0].orgGuid;
}

export function writeOrgGuidToLocalStorage(userGuid: string | null, orgGuid: string) {
  localStorage.setItem(`preferred_org_guid_${userGuid}`, orgGuid);
}

function readOrgGuidFromQueryParam() {
  const query = window.location.href.split('?').pop();
  const params = new URLSearchParams(query);
  return params.get('ts_org_guid');
}

function redirectToAuth(config: TellentClientAuthEndpoints) {
  if (window.isNativeMobileHr) {
    window.nativeMobileHr.openInAppBrowser();
  } else {
    window.open(`${config.tellentAuthUrl}?redirect_to=${window.location.href}`, '_self');
  }
}

function resolveMember(user: TellentCurrentUser, orgGuid: string): TellentMember | undefined {
  return user.members.find(m => m.orgGuid === orgGuid);
}

function resolveAppGuid(member: TellentMember, appKind: string) {
  const seat = member.seats.find(s => s.app.kind === appKind);

  return seat?.appGuid;
}

function validateOrgGuid(user: TellentCurrentUser, orgGuid: string): boolean {
  return user.members.some(m => m.orgGuid === orgGuid);
}
